import React from 'react';
import { View, StyleSheet, Pressable, Text } from 'react-native';
import Typography from '../../components/shared/Typography/Typography.component';
import I18n from '../../components/shared/i18n/I18n.component';
import colors from '../../config/colors';
import { convertFromUtc } from '../../pages/HoursReportSummary/EmployeeHoursReport/EmployeeHoursReport.utils';
import { UPDATE_REASON_OPTIONS } from '../AddRequestModal/AddRequestModal.utils';

interface InfoEmployeeModalProps {
	rowData: any;
	updatedBy: string;
	enterUpdateBy: string;
	exitUpdateBy: string;
	updateDate: string;
	enterLastUpdate: string;
	exitLastUpdate: string;
	oldEnterTime: string;
	enterTime: string;
	exitTime: string;
	oldExitTime: string;
	enterUpdateReason: string;
	enterUpdateReasonText: string;
	exitUpdateReason: string;
	exitUpdateReasonText: string;
	onClose: () => void;
}

const InfoEmployeeModal = ({
	rowData,
	updatedBy,
	enterUpdateBy,
	exitUpdateBy,
	updateDate,
	enterLastUpdate,
	exitLastUpdate,
	oldEnterTime,
	enterTime,
	exitTime,
	oldExitTime,
	enterUpdateReason,
	enterUpdateReasonText,
	exitUpdateReason,
	exitUpdateReasonText,
	onClose,
}: InfoEmployeeModalProps) => {
	const oldEnterTimeFormatted = convertFromUtc(oldEnterTime, true);
	const oldExitTimeFormatted = convertFromUtc(oldExitTime, true);
	const newEnterTimeFormatted = convertFromUtc(enterTime, true);
	const newExitTimeFormatted = convertFromUtc(exitTime, true);
	return (
		<View style={styles.container}>
			<View style={styles.header}>
				<Pressable onPress={onClose} style={styles.closeButton}>
					<Text style={styles.closeButtonText}>×</Text>
				</Pressable>
				<I18n size={20} weight='bold' color='primaryDarkBlue'>
					modal.infoEmployee.title
				</I18n>
			</View>

			<View style={styles.section}>
				<View style={styles.timeRow}>

					<View style={styles.timeColumn}>
						<I18n size={14} weight='bold' color='primaryDarkBlue'>
							modal.infoEmployee.fromEntryTime
						</I18n>
						<Typography size={14} weight='normal' color='clockRecordBlue'>
							{oldEnterTimeFormatted}
						</Typography>
					</View>

					<View style={styles.arrow}>
						<Text style={styles.arrowText}>←</Text>
					</View>

					<View style={styles.timeColumn}>
						<I18n size={14} weight='bold' color='primaryDarkBlue'>
							modal.infoEmployee.toEntryTime
						</I18n>
						<Typography size={14} weight='normal' color='clockRecordBlue'>
							{newEnterTimeFormatted}
						</Typography>
					</View>
				</View>

				<View style={styles.metaRow}>
					<View style={styles.metaItem}>
						<I18n size={14} weight='bold' color='primaryDarkBlue'>
							modal.infoEmployee.updatedBy
						</I18n>
						<Typography size={14} weight='normal' color='clockRecordBlue'>
							{enterUpdateBy || "-"}
						</Typography>
					</View>
					<View style={styles.metaItem}>
						<I18n size={14} weight='bold' color='primaryDarkBlue'>
							modal.infoEmployee.onDate
						</I18n>
						<Typography size={14} weight='normal' color='clockRecordBlue'>
							{enterLastUpdate || "-"}
						</Typography>
					</View>
				</View>

				{(enterUpdateReason !== 'OTHER' || enterUpdateReasonText) && (
					<View style={styles.reasonBox}>
						<I18n size={14} weight='bold' color='primaryDarkBlue'>
							modal.infoEmployee.reason
						</I18n>
						{enterUpdateReason !== 'OTHER' && (
						<Typography size={14} weight='normal' color='clockRecordBlue'>
								{UPDATE_REASON_OPTIONS.find(option => option.id === enterUpdateReason)?.name || "-"}
							</Typography>
						)}
						{enterUpdateReasonText && (
						<Typography size={14} weight='normal' color='clockRecordBlue'>
								{enterUpdateReasonText}
							</Typography>
						)}
					</View>
				)}
			</View>

			<View style={styles.divider} />

			<View style={styles.section}>
				<View style={styles.timeRow}>
					<View style={styles.timeColumn}>
						<I18n size={14} weight='bold' color='primaryDarkBlue'>
							modal.infoEmployee.fromExitTime
						</I18n>
						<Typography size={14} weight='normal' color='clockRecordBlue'>
							{oldExitTimeFormatted}
						</Typography>
					</View>
					<View style={styles.arrow}>
						<Text style={styles.arrowText}>←</Text>
					</View>
					<View style={styles.timeColumn}>
						<I18n size={14} weight='bold' color='primaryDarkBlue'>
							modal.infoEmployee.toExitTime
						</I18n>
						<Typography size={14} weight='normal' color='clockRecordBlue'>
							{newExitTimeFormatted}
						</Typography>
					</View>
				</View>

				<View style={styles.metaRow}>
					<View style={styles.metaItem}>
						<I18n size={14} weight='bold' color='primaryDarkBlue'>
							modal.infoEmployee.updatedBy
						</I18n>
						<Typography size={14} weight='normal' color='clockRecordBlue'>
							{exitUpdateBy || "-"}
						</Typography>
					</View>
					<View style={styles.metaItem}>
						<I18n size={14} weight='bold' color='primaryDarkBlue'>
							modal.infoEmployee.onDate
						</I18n>
						<Typography size={14} weight='normal' color='clockRecordBlue'>
							{exitLastUpdate || "-"}
						</Typography>
					</View>
				</View>

				{(exitUpdateReason !== 'OTHER' || exitUpdateReasonText) && (
					<View style={styles.reasonBox}>
						<I18n size={14} weight='bold' color='primaryDarkBlue'>
							modal.infoEmployee.reason
						</I18n>
						{exitUpdateReason !== 'OTHER' && (
							<Typography size={14} weight='normal' color='clockRecordBlue'>
								{UPDATE_REASON_OPTIONS.find(option => option.id === exitUpdateReason)?.name || "-"}
							</Typography>
						)}
						{exitUpdateReasonText && (
							<Typography size={14} weight='normal' color='clockRecordBlue'>
								{exitUpdateReasonText}
							</Typography>
						)}
					</View>
				)}
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: colors.white,
		borderRadius: 12,
		padding: 32,
		minWidth: 380,
		maxWidth: 450,
		gap: 24,
	},
	header: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 16,
	},
	closeButton: {
		padding: 4,
		marginLeft: -8,
	},
	closeButtonText: {
		fontSize: 28,
		color: colors.darkGrey,
		lineHeight: 28,
	},
	section: {
		display: 'flex',
		flexDirection: 'column',
		gap: 24,
	},
	timeRow: {
		display: 'flex',
		flexDirection: 'row-reverse',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 12,
	},
	timeColumn: {
		display: 'flex',
		flexDirection: 'row-reverse',
		flex: 1,
		gap: 4,
	},
	arrow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	arrowText: {
		fontSize: 16,
		color: colors.darkGrey,
	},
	metaRow: {
		display: 'flex',
		flexDirection: 'row-reverse',
		justifyContent: 'space-between',
		gap: 12,
	},
	metaItem: {
		display: 'flex',
		flexDirection: 'row-reverse',
		gap: 2,
		flex: 1,
	},
	reasonBox: {
		display: 'flex',
		flexDirection: 'row-reverse',
		gap: 8,
	},
	divider: {
		height: 1,
		backgroundColor: '#e8e8e8',
		marginVertical: 8,
	},
});

export default InfoEmployeeModal;
